import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../context/notificationContext";
import { useUserSettings } from "../../../context/userSettingsContext";
import useDataInitializer from "../../../hooks/useDataInitializer";
import { useI18n } from "../../../hooks/useTranslations";
import { useDueDateTypesApi } from "../../../http/dueDateTypes/api";
import { UpsertDueDateType } from "../../../http/dueDateTypes/types";
import ModalDialog from "../../ui/ModalDialog";
import DueDateTypeForm, { DueDateTypeFormBody } from "./DueDateTypeForm";

type EditDueDateTypeDialogProps = {
  dueDateTypeId: string;
  onClose: () => void;
};

const EditDueDateTypeDialog: FC<EditDueDateTypeDialogProps> = ({
  dueDateTypeId,
  onClose,
}: EditDueDateTypeDialogProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dueDateTypesApi = useDueDateTypesApi();

  const dueDateTypeSwr = dueDateTypesApi.getAdminDueDateType(dueDateTypeId, aborter.current.signal);
  const [dueDateType, isInitialized] = useDataInitializer(dueDateTypeSwr.data, dueDateTypeSwr.error);

  useEffect(() => {
    dueDateTypeId && dueDateTypeSwr.trigger();
  }, [dueDateTypeId]);

  useEffect(() => {
    dueDateTypeSwr.error && dispatch(showErrorNotification(i18n, dueDateTypeSwr.error));
  }, [dueDateTypeSwr.error]);

  const onSubmit = async (formData: DueDateTypeFormBody): Promise<void> => {
    if (dueDateType?.id) {
      setIsSubmitting(true);

      const body: UpsertDueDateType = {
        name: formData.name,
        currentVersionNumber: dueDateType.versionNumber,
      };

      try {
        await dueDateTypesApi.updateAdminDueDateType(dueDateType.id, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.t.dueDateTypes.edit}>
      <DueDateTypeForm
        onSubmit={onSubmit}
        isDisabled={!userSettings.permissions.dueDateTypes.update || isSubmitting || !dueDateType?.id}
        isLoading={!isInitialized}
        dueDateType={dueDateType}
      />
    </ModalDialog>
  );
};

export default EditDueDateTypeDialog;
